// Registro en memoria de las FOTOS que el usuario engancha en montón en el chat —
// ADR-2026-09-08-el-chat-trabaja-en-lote y
// ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez.
//
// Son 7 o más fotos elegidas de una vez. No viajan al modelo al enviar: el mensaje lleva
// sólo una línea con el `lote_id` y cuántas son. Es memoria de la pestaña a propósito: un
// `File` no se serializa, y subirlas todas "por las dudas" sería pagar 80 uploads por
// fotos que el usuario todavía no dijo para qué son. Tras una recarga el lote no está: las
// tarjetas y `ver_fotos` lo dicen y ofrecen volver a elegirlas.
//
// Lo que NO son es "un lote de productos". Eso era la lectura vieja —muchas fotos ⇒
// productos— y decidía por el usuario: con 20 fotos y un "poneme fondo blanco a cada una"
// el agente no podía ni verlas. Ahora son un CONJUNTO de fotos y lo que se hace con ellas
// lo dice la instrucción. El nombre `lote` quedó en el cable (el `lote_id` de la tool y
// los adjuntos ya persistidos en `agent_chats`): renombrarlo rompería los chats viejos al
// reanudarlos, y no vale ese precio.
//
// `storagePath` se llena a demanda: la primera herramienta que necesita una foto de
// verdad la sube (`subirFotosDelLote.ts`) y la anota acá, así mirarla dos veces —o mirarla
// y después usarla como referencia— no la sube dos veces.

import { idDeFoto } from './organizarProductos';
import { MAX_FOTOS_A_VER } from './verFotos';

export interface FotoDelLote {
  /** `f0`, `f1`… — el mismo id con el que las agrupa `organizarProductos`. */
  id: string;
  archivo: File;
  /** Path en el storage, una vez que alguna herramienta la subió. */
  storagePath?: string;
}

export interface LoteDeFotos {
  id: string;
  archivos: File[];
  creadoEn: number;
  /** fotoId → storagePath de las que ya se subieron. */
  subidas: Map<string, string>;
}

const registro = new Map<string, LoteDeFotos>();

const nuevoId = (): string => {
  try {
    return `lote-${crypto.randomUUID().slice(0, 8)}`;
  } catch {
    return `lote-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  }
};

export function registrarLoteDeFotos(archivos: File[], ahora: number = Date.now()): LoteDeFotos {
  const lote: LoteDeFotos = {
    id: nuevoId(),
    archivos: [...archivos],
    creadoEn: ahora,
    subidas: new Map(),
  };
  registro.set(lote.id, lote);
  return lote;
}

export function obtenerLoteDeFotos(id: string | null | undefined): LoteDeFotos | null {
  if (!id) return null;
  return registro.get(id) ?? null;
}

export function liberarLoteDeFotos(id: string | null | undefined): void {
  if (id) registro.delete(id);
}

/** Las fotos del lote con su id y lo que ya se subió. Vacío si el lote no está. */
export function fotosDelLote(id: string | null | undefined): FotoDelLote[] {
  const lote = obtenerLoteDeFotos(id);
  if (!lote) return [];
  return lote.archivos.map((archivo, i) => {
    const fotoId = idDeFoto(i);
    const storagePath = lote.subidas.get(fotoId);
    return { id: fotoId, archivo, ...(storagePath ? { storagePath } : {}) };
  });
}

/** El path de una foto ya subida, o null si todavía no se subió. */
export function pathDeFoto(
  loteId: string | null | undefined,
  fotoId: string,
): string | null {
  return obtenerLoteDeFotos(loteId)?.subidas.get(fotoId) ?? null;
}

export function marcarFotoSubida(
  loteId: string | null | undefined,
  fotoId: string,
  storagePath: string,
): void {
  obtenerLoteDeFotos(loteId)?.subidas.set(fotoId, storagePath);
}

/** Sólo para tests. */
export function _vaciarRegistroDeLotes(): void {
  registro.clear();
}

/**
 * La línea que va en el bloque de contexto del mensaje del usuario. El modelo no ve las
 * fotos, pero sabe cuántas son, cómo se llaman y qué puede hacer con ellas.
 *
 * La última frase no sobra: la versión anterior de esta línea le decía al modelo que un
 * montón de fotos era una carga de productos, y con eso alcanzaba para que ignorara lo que
 * el usuario había pedido.
 */
export function lineaDeContextoDelLote(lote: { id: string; archivos: { length: number } }): string {
  const n = lote.archivos.length;
  return [
    `El usuario adjuntó ${n} FOTOS de una vez (foto_id "f0" a "f${n - 1}", en ese orden; lote_id="${lote.id}"). No las ves acá: son demasiadas para que viajen en el mensaje.`,
    `Para MIRARLAS: ver_fotos, de a ${MAX_FOTOS_A_VER} (desde=1, después desde=${MAX_FOTOS_A_VER + 1}…). Llamala sólo si necesitás ver qué hay para decidir; no las mires todas por las dudas.`,
    `Para USAR una como referencia de una generación: en generar_lote_imagenes, source "adjunto" + su \`foto_id\`.`,
    `Para SUMARLAS al catálogo como productos: agregar_productos con lote_id "${lote.id}" — van TODAS juntas, no por tramos: la card las agrupa por producto y agruparlas de a diez separaría mal un producto que tiene fotos en dos tramos.`,
    `Que sean muchas NO quiere decir que sean productos: hacé lo que pidió el usuario.`,
  ].join(' ');
}
